import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faEye,
    faPrint,
    faCheck,
    faTimes,
    faSpinner,
    faShoppingCart,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { USER_ROLES } from '../../utils/constants';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';

const formatDate = (dateString, t) => {
    if (!dateString) return t('common.symbols.emDash');
    return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const getStatusBadge = (status, t) => {
    const s = (status || '').toString().toLowerCase();
    const getLabel = (key, fallback) => {
        if (!key) return fallback || String(status);
        const statuses = t('purchaseChallan.statuses', { returnObjects: true });
        return (statuses && statuses[key]) || fallback || String(status);
    };

    if (s === 'completed' || s === 'received' || s === 'loaded' || s === 'challan_generated') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (s === 'cancelled' || s === 'rejected') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faTimes} className="text-sm" style={{ color: UI_COLORS.STATUS.ERROR }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (s === 'in_transit' || s === 'advice_created' || s === 'confirmed') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (s === 'loading') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faSpinner} spin className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }

    return (
        <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faShoppingCart} className="text-sm" style={{ color: UI_COLORS.STATUS.WARNING }} />
            <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>{getLabel(s, String(status))}</span>
        </div>
    );
};

const PurchaseChallanPrintedTable = ({
    challans = [],
    loading = false,
    onViewChallan,
    onEditChallan,
    onPrintChallan,
    userRole,
    viewLoading = false,
}) => {
    const { t } = useTranslation();
    const isAdminOrSuperAdmin = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole);
    const canEdit = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN].includes(userRole);

    const columns = useMemo(() => [
        {
            id: 'po_number',
            label: t('purchaseChallan.table.columns.poNumber'),
            sortable: true,
            sortAccessor: (c) => (c?.po_number || '').toLowerCase(),
            renderCell: (c) => <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{c.po_number || t('common.symbols.emDash')}</div>,
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'advice_number',
            label: t('purchaseAdvice.table.columns.adviceNumber'),
            sortable: true,
            sortAccessor: (c) => (c?.advice_number || '').toLowerCase(),
            renderCell: (c) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{c.advice_number || t('common.symbols.emDash')}</div>
            ),
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'challan_number',
            label: t('purchaseChallan.table.columns.challanNumber'),
            sortable: true,
            sortAccessor: (c) => (c?.challan_number || '').toLowerCase(),
            renderCell: (c) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{c.challan_number || t('common.symbols.emDash')}</div>
            ),
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'advice_date',
            label: t('purchaseAdvice.table.columns.date'),
            sortable: true,
            sortAccessor: (c) => (c?.advice_date ? new Date(c.advice_date).getTime() : 0),
            renderCell: (c) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{c.advice_date ? formatDate(c.advice_date, t) : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'supplier_name',
            label: t('purchaseAdvice.view.fields.supplierName'),
            sortable: true,
            sortAccessor: (c) => (c?.supplier_name || '').toLowerCase(),
            renderCell: (c) => <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{c.supplier_name || t('common.symbols.emDash')}</div>,
            skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'vehicle_number',
            label: t('purchaseAdvice.table.columns.vehicleNumber'),
            sortable: true,
            sortAccessor: (c) => (c?.vehicle_number || '').toLowerCase(),
            renderCell: (c) => <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{c.vehicle_number || t('common.symbols.emDash')}</div>,
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'total_quantity',
            label: t('common.formFields.labels.totalQuantity'),
            sortable: true,
            sortAccessor: (c) => (c?.total_quantity ? parseFloat(c.total_quantity) : 0),
            renderCell: (c) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{c.total_quantity != null ? c.total_quantity : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-16 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'po_remark',
            label: t('commonCols.purchaseOrderRemarks'),
            sortable: true,
            sortAccessor: (c) => (c?.po_remark || '').toLowerCase(),
            renderCell: (c) => {
                if (!c.po_remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.symbols.emDash')}</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
                        title={c.po_remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {c.po_remark}
                    </div>
                );
            },
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'status',
            label: t('purchaseChallan.table.columns.status'),
            sortable: true,
            align: 'center',
            justify: 'center',
            sortAccessor: (c) => (c?.status || '').toLowerCase(),
            renderCell: (c) => getStatusBadge(c.status, t),
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'actions',
            label: t('purchaseChallan.table.columns.actions'),
            align: 'center',
            sortable: false,
            renderCell: (c) => (
                <div className="flex items-center justify-center space-x-2">
                    <button
                        onClick={() => onViewChallan?.(c.challan_number || c.id)}
                        disabled={viewLoading}
                        className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50"
                        style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
                        title={t('purchaseChallan.table.tooltips.view')}
                    >
                        <FontAwesomeIcon icon={faEye} className="text-sm" />
                    </button>

                    {canEdit && (
                        <button
                            onClick={() => onEditChallan?.(c.challan_number || c.id)}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ 
                                color: UI_COLORS.STATUS.WARNING, 
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) 
                            }}
                            title={t('purchaseChallan.table.tooltips.edit')}
                        >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                        </button>
                    )}

                    {isAdminOrSuperAdmin && (
                        <button
                            onClick={() => onPrintChallan?.(c.challan_number || c.id)}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
                            title="Reprint Challan"
                        >
                            <FontAwesomeIcon icon={faPrint} className="text-sm" />
                        </button>
                    )}
                </div>
            ),
        },
    ], [onViewChallan, onEditChallan, onPrintChallan, viewLoading, isAdminOrSuperAdmin, canEdit, t]);

    return (
        <DataTable
            data={challans}
            columns={columns}
            loading={loading}
            enablePagination={false}
            defaultSort={{ field: 'updated_at', direction: 'desc' }}
            keyField="id"
            emptyTitle="No Printed Challans"
            emptyMessage="No purchase challans have been printed yet."
        />
    );
};

export default PurchaseChallanPrintedTable;
